import { Product, PRODUCT_CONFIG_STATUSES, ProductConfig, ProductInput } from '@vy/types';
import {
  buildPeriod,
  extract,
  isEmptyArray,
  isNotEmptyString,
  isNotNullOrUndefined,
  isNullOrUndefined,
  Period,
  requiredAt,
} from '@vy/utils';
import { max, min } from 'lodash';
import { PeriodsData } from '../ContractPhasesTimeline/ContractPhasesTimelineContainer';

export function buildProductPeriodsData(
  defaultPeriod: Period,
  products: (Product | ProductInput)[],
): PeriodsData[] {
  return products.map<PeriodsData>((product, index) => {
    const { variants } = product;
    const mainVariant = requiredAt(variants, 0);
    const period = buildProductActivePeriod(defaultPeriod, product);
    const title = isNotEmptyString(mainVariant.name) ? mainVariant.name : `Unnamed Product ${index + 1}`;

    return {
      title,
      period,
      pricingModel: mainVariant.pricing.pricingModel,
    };
  });
}

function buildProductActivePeriod(fallbackPeriod: Period, product: Product | ProductInput): Period {
  const { productConfigs, startDate, endDate } = product;

  const maxConfigSpan = isNotNullOrUndefined(product.revisionId)
    ? getMaxConfigsSpan(productConfigs)
    : undefined;

  const activeProductStartDate = startDate ?? maxConfigSpan?.startTime ?? fallbackPeriod.startTime;
  const activeProductEndDate = endDate ?? maxConfigSpan?.endTime ?? fallbackPeriod.endTime;

  return {
    startTime: activeProductStartDate,
    endTime: activeProductEndDate,
  };
}

function getMaxConfigsSpan(productConfigs: ProductConfig[] | undefined) {
  if (isEmptyArray(productConfigs)) {
    return undefined;
  }

  const activeConfigs = productConfigs.filter(isActiveConfig);

  const startTimes = extract(activeConfigs, (config) => config.period.startTime);
  const endTimes = extract(activeConfigs, (config) => config.period.endTime);

  const minStartTime = min(startTimes);
  const maxEndTime = max(endTimes);

  return isNullOrUndefined(minStartTime) || isNullOrUndefined(maxEndTime)
    ? undefined
    : buildPeriod(minStartTime, maxEndTime);
}

function isActiveConfig(config: ProductConfig): boolean {
  return config.status === PRODUCT_CONFIG_STATUSES.ACTIVE;
}
